import React, { useEffect, useState } from 'react';
import { ChevronDown, ChevronRight, UserPlus, AlertTriangle, CheckCircle } from 'lucide-react';
import { format, parseISO } from 'date-fns';
import api from '../../api/axios';
import Modal from '../common/Modal';
import { PersonName } from '../common/PersonColor';

// Admin tools for open slots: result of "Fill open shifts" and the
// "Who could take this?" candidate list. Values come from the server, which
// uses the generator's own eligibility and scoring rules.

export const UNFILLED_REASON = {
  nobody_available: 'Nobody available',
  nobody_qualified: 'Nobody qualified',
  all_at_max: 'All candidates at max',
  legal_limits: 'Would break 10 h day / 11 h rest',
  max_streak: 'Would exceed the max days in a row',
  minijob_cap: 'Minijob cap reached',
  event_rotation: 'Event rotation (worked it last month)',
};

export const BLOCK_REASON = {
  not_qualified: 'Not qualified',
  already_working: 'Already working that day',
  overlap: 'Overlaps another assignment',
  absence: 'Absent',
  recurring_block: 'Recurring weekday block',
  vacation: 'On vacation',
  half_day_block: 'Half day blocked',
  weekend_rhythm: 'Free weekend (rhythm)',
  blocked_day: 'Blocked day',
  pms_day: 'Blocked day',
  blocked_weekend: 'Blocked weekend',
  sick: 'Sick',
  minijob_cap: 'Minijob cap reached',
  event_rotation: 'Event rotation (worked it last month)',
  daily_hours: 'Would work more than 10 h that day',
  max_streak: 'Would exceed the max days in a row',
  rest_period: 'Less than 11 h rest to another shift',
};

const WARNING = {
  emergency_range: { label: 'Emergency range', color: '#E65100' },
  above_max: { label: 'Above max', color: '#C62828' },
  minijob_cap_close: { label: 'Minijob cap close', color: '#E65100' },
  blocked_day_override: { label: 'Blocked day (event overrides)', color: '#E65100' },
  max_streak: { label: 'Over the max days in a row', color: '#C62828' },
};

// "Would exceed 5 days in a row" — with the number when it is known
export const maxDaysReason = (max) => (max ? `Would exceed ${max} days in a row` : UNFILLED_REASON.max_streak);

const fmtTime = (t) => (t ? t.slice(0, 5) : '');
const fmtDate = (d) => (d ? format(parseISO(d), 'EEE, d MMM') : '');
const slotLabel = (s) => `${fmtDate(s.date)} · ${s.is_event ? '🧹 ' : ''}${s.template_name || 'Shift'} ${fmtTime(s.start_time)}–${fmtTime(s.end_time)}`;

const listStyle = {
  listStyle: 'none', margin: 0, padding: 0, border: '1px solid var(--border)', borderRadius: 'var(--radius)',
  maxHeight: '260px', overflowY: 'auto',
};
const itemStyle = { padding: '7px 10px', borderBottom: '1px solid var(--border)', fontSize: '0.85rem', display: 'flex', gap: '8px', flexWrap: 'wrap', alignItems: 'baseline' };

// ── Result of a fill run ──────────────────────────────────────────────────────
export function FillResultModal({ result, onClose, onUndo, undoing }) {
  if (!result) return null;
  const { filled, total_open: total, assignments = [], unfilled = [] } = result;
  const allDone = total > 0 && filled === total;
  return (
    <Modal
      isOpen
      onClose={onClose}
      title="Fill open shifts"
      size="md"
      footer={
        <div style={{ display: 'flex', justifyContent: 'space-between', gap: '8px', flexWrap: 'wrap' }}>
          {filled > 0 && onUndo ? (
            <button className="btn btn-secondary" onClick={onUndo} disabled={undoing}>
              {undoing ? 'Undoing…' : 'Undo this fill'}
            </button>
          ) : <span />}
          <button className="btn btn-primary" onClick={onClose}>Close</button>
        </div>
      }
    >
      <div
        style={{
          display: 'flex', alignItems: 'center', gap: '8px', padding: '12px 14px', marginBottom: '16px',
          borderRadius: 'var(--radius)', fontWeight: 700,
          background: allDone ? 'var(--success-light)' : 'var(--primary-xlight)',
          border: `1px solid ${allDone ? 'var(--success)' : 'var(--primary-light)'}`,
        }}
      >
        {allDone ? <CheckCircle size={16} style={{ color: 'var(--success)' }} /> : <UserPlus size={16} style={{ color: 'var(--primary-dark)' }} />}
        {total === 0 ? 'There were no open shifts to fill.' : `Filled ${filled} of ${total} open ${total === 1 ? 'shift' : 'shifts'}`}
      </div>
      {assignments.some((a) => a.streak_override) && (
        <p role="alert" style={{ margin: '0 0 14px', padding: '10px 12px', borderRadius: 'var(--radius)', background: 'var(--danger-light)', border: '1px solid var(--danger)', color: 'var(--danger)', fontWeight: 700, fontSize: '0.85rem' }}>
          {assignments.filter((a) => a.streak_override).length} assignment{assignments.filter((a) => a.streak_override).length === 1 ? ' goes' : 's go'} over
          the max days in a row (emergency) — marked red below and recorded in the audit log.
        </p>
      )}

      {assignments.length > 0 && (
        <div style={{ marginBottom: '16px' }}>
          <h4 style={{ fontSize: '0.9rem', marginBottom: '6px' }}>New assignments ({assignments.length})</h4>
          <ul style={listStyle}>
            {[...assignments].sort((x, y) => !!y.streak_override - !!x.streak_override).map((a) => (
              <li
                key={a.slot_id}
                style={a.streak_override ? { ...itemStyle, background: 'var(--danger-light)', borderLeft: '4px solid var(--danger)' } : itemStyle}
              >
                <PersonName userId={a.user_id} name={a.user_name} style={{ fontWeight: 700 }} />
                <span style={{ color: 'var(--text-light)' }}>{slotLabel(a)}</span>
                {a.streak_override && (
                  <span style={{ color: 'var(--danger)', fontWeight: 700, fontSize: '0.8rem', display: 'inline-flex', alignItems: 'center', gap: '3px' }}>
                    <AlertTriangle size={12} /> Over the limit: {a.streak_override.days} days in a row (max {a.streak_override.max})
                  </span>
                )}
              </li>
            ))}
          </ul>
        </div>
      )}

      {unfilled.length > 0 && (
        <div>
          <h4 style={{ fontSize: '0.9rem', marginBottom: '6px' }}>Still open ({unfilled.length})</h4>
          <ul style={listStyle}>
            {unfilled.map((u) => (
              <li key={u.slot_id} style={itemStyle}>
                <span>{slotLabel(u)}</span>
                <span style={{ color: '#C62828', fontWeight: 600 }}>
                  {u.reason === 'max_streak' ? maxDaysReason(result.max_days_in_row) : (UNFILLED_REASON[u.reason] || u.reason)}
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </Modal>
  );
}

// ── "Who could take this?" (inside the slot modal for unassigned slots) ──────
export function SlotCandidates({ slotId, onAssign, assigning }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showBlocked, setShowBlocked] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    api.get(`/shifts/slots/${slotId}/candidates`)
      .then((res) => { if (!cancelled) setData(res.data); })
      .catch((err) => { if (!cancelled) setError(err.response?.data?.error || 'Failed to load candidates.'); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [slotId]);

  return (
    <div
      style={{
        border: '1px solid var(--primary-light)', background: 'var(--primary-xlight)', borderRadius: 'var(--radius)',
        padding: '12px', marginBottom: '16px',
      }}
    >
      <h4 style={{ fontSize: '0.92rem', fontWeight: 700, marginBottom: '8px', display: 'flex', alignItems: 'center', gap: '6px' }}>
        <UserPlus size={15} style={{ color: 'var(--primary-dark)' }} /> Who could take this?
      </h4>
      {loading && <div className="spinner" style={{ width: 16, height: 16, borderWidth: 2 }} />}
      {error && <p style={{ fontSize: '0.85rem', color: 'var(--danger)' }}>{error}</p>}
      {data && (
        <>
          {data.available.length === 0 ? (
            <p style={{ fontSize: '0.85rem', color: 'var(--text-muted)', marginBottom: '6px' }}>Nobody passes the hard blocks for this shift.</p>
          ) : (
            <ul style={{ ...listStyle, background: 'var(--white)' }}>
              {data.available.map((c) => (
                <li key={c.user_id} style={{ ...itemStyle, alignItems: 'center', justifyContent: 'space-between' }}>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '2px', minWidth: 0 }}>
                    <span>
                      <PersonName userId={c.user_id} style={{ fontWeight: 700 }}>{c.name}</PersonName>
                      <span style={{ color: 'var(--text-light)' }}>
                        {' · '}{c.shifts} / max {c.emergency_max}
                        {c.extra_shifts > 0 && ` (+${c.extra_shifts} extra)`}
                        {' · '}{c.regular_hours} h
                        {c.cap_hours != null && ` · ${c.all_hours} / ${c.cap_hours} h cap`}
                      </span>
                    </span>
                    {c.warnings.length > 0 && (
                      <span style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                        {c.warnings.map((w) => (
                          <span key={w} style={{ display: 'inline-flex', alignItems: 'center', gap: '3px', fontSize: '0.72rem', fontWeight: 600, color: WARNING[w]?.color || '#E65100' }}>
                            <AlertTriangle size={11} />
                            {w === 'max_streak' && c.over_limit
                              ? `Would be ${c.over_limit.days} days in a row (max ${c.over_limit.max})`
                              : (WARNING[w]?.label || w)}
                          </span>
                        ))}
                      </span>
                    )}
                  </div>
                  <button
                    type="button"
                    className="btn btn-primary btn-sm"
                    onClick={() => onAssign(c.user_id)}
                    disabled={assigning}
                    style={{ flexShrink: 0 }}
                  >
                    Assign
                  </button>
                </li>
              ))}
            </ul>
          )}

          {data.blocked.length > 0 && (
            <div style={{ marginTop: '8px' }}>
              <button
                type="button"
                onClick={() => setShowBlocked((v) => !v)}
                aria-expanded={showBlocked}
                style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', background: 'none', border: 'none', padding: 0, cursor: 'pointer', fontSize: '0.8rem', color: 'var(--text-light)' }}
              >
                {showBlocked ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                Not available ({data.blocked.length})
              </button>
              {showBlocked && (
                <ul style={{ ...listStyle, marginTop: '6px', background: 'var(--white)' }}>
                  {data.blocked.map((b) => (
                    <li key={b.user_id} style={{ ...itemStyle, justifyContent: 'space-between', color: 'var(--text-muted)' }}>
                      <PersonName userId={b.user_id} name={b.name} />
                      <span>{BLOCK_REASON[b.reason] || b.reason}</span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
}
